import React, { useState, useEffect, useRef, useCallback } from 'react';
import { LoginForm } from './components/LoginForm';
import { UserLogin } from './components/UserLogin';
import { Dashboard } from './components/Dashboard';
import { AccountSelector } from './components/AccountSelector';
import { ConfirmDialog } from './components/ConfirmDialog';
import { ToastContainer } from './components/Toast';
import { ErrorBoundary } from './components/ErrorBoundary';
import { ThemeToggle } from './components/ThemeToggle';
import './App.css';
import { fetchAllInvoiceData, processCSVDataForVisualizations, describeDigitalOceanError } from './utils/csvUtils';
import { saveData, loadData, clearAccountData, getCacheStatus, describeCacheEntry } from './utils/storageUtils';
import { filterLineItemsByTimeRange } from './utils/dataUtils';
import { apiRequest } from './utils/apiClient';

const ACCOUNTS_KEY = 'doAccounts';
// Cached data older than this is refreshed in the background when shown.
const BACKGROUND_REFRESH_AFTER_HOURS = 20;

// Accounts configured through env-config.js or REACT_APP_* variables.
// Kept for local development; in production the server provides its
// configured accounts through /api/accounts after sign-in.
export const parseEnvironmentAccounts = () => {
  const tokens = window.ENV_VARS?.DO_API_TOKENS ||
                 process.env.REACT_APP_DO_API_TOKENS ||
                 null;

  const names = window.ENV_VARS?.DO_ACCOUNT_NAMES ||
                process.env.REACT_APP_DO_ACCOUNT_NAMES ||
                null;

  if (tokens && names) {
    try {
      const tokenArray = tokens.split(',');
      const nameArray = names.split(',');

      // Ensure we have matching numbers of tokens and names
      if (tokenArray.length !== nameArray.length) {
        console.error('Mismatch between number of tokens and account names in environment variables');
        return null;
      }

      // Create account objects
      return tokenArray.map((token, index) => ({
        name: nameArray[index].trim(),
        token: token.trim()
      }));
    } catch (error) {
      console.error('Error parsing environment accounts:', error);
      return null;
    }
  }

  return null;
};

const isValidAccount = (account) =>
  Boolean(account) && typeof account.name === 'string' && account.name !== '' &&
  typeof account.token === 'string' && account.token !== '';

const readStoredAccounts = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem(ACCOUNTS_KEY) || '[]');
    return Array.isArray(parsed) ? parsed.filter(isValidAccount) : [];
  } catch (e) {
    return [];
  }
};

// Server-configured ("managed") accounts first, then accounts the user added
// in this browser. Names identify accounts, so a managed name wins.
export const mergeAccounts = (managedAccounts, storedAccounts) => {
  const seen = new Set();
  const merged = [];
  managedAccounts.filter(isValidAccount).forEach((account) => {
    const key = account.name.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    merged.push({ name: account.name, token: account.token, managed: true });
  });
  storedAccounts.filter(isValidAccount).forEach((account) => {
    const key = account.name.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    merged.push({ name: account.name, token: account.token });
  });
  return merged;
};

// Only accounts the user added are kept in the browser; managed accounts
// are fetched from the server on every sign-in.
const persistAccounts = (accounts) => {
  const own = accounts.filter((account) => !account.managed).map(({ name, token }) => ({ name, token }));
  try {
    if (own.length > 0) localStorage.setItem(ACCOUNTS_KEY, JSON.stringify(own));
    else localStorage.removeItem(ACCOUNTS_KEY);
  } catch (e) {
    console.error('Unable to save accounts in this browser:', e);
  }
};

// Earlier versions kept sign-in state and TOTP secrets in browser storage.
const removeLegacyAuthStorage = () => {
  [localStorage, sessionStorage].forEach((storage) => {
    try {
      ['appAuth', 'rememberDevice', 'app_2fa_secret'].forEach((key) => storage.removeItem(key));
      Object.keys(storage)
        .filter((key) => key.startsWith('totpSecret:'))
        .forEach((key) => storage.removeItem(key));
    } catch (e) {}
  });
};

let toastCounter = 0;

function App() {
  // Sign-in state, decided by the backend session
  const [authState, setAuthState] = useState('checking'); // 'checking' | 'signedOut' | 'signedIn' | 'unreachable'
  const [authMessage, setAuthMessage] = useState('');
  const [user, setUser] = useState(null);

  // Multi-account state
  const [accounts, setAccounts] = useState([]);
  const [accountsLoaded, setAccountsLoaded] = useState(false);
  const [currentAccountIndex, setCurrentAccountIndex] = useState(0);

  // In-memory data per account, so switching back does not reload
  const [accountsData, setAccountsData] = useState({});

  const [allInvoices, setAllInvoices] = useState([]);
  const [detailedLineItems, setDetailedLineItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadProgress, setLoadProgress] = useState({ done: 0, total: 0 });
  const [error, setError] = useState('');
  const [warning, setWarning] = useState('');
  const [timeRange, setTimeRange] = useState('1month');
  const [statusMessage, setStatusMessage] = useState('');
  const [processedData, setProcessedData] = useState(null);
  const [cacheStatus, setCacheStatus] = useState({ isCached: false });

  const [toasts, setToasts] = useState([]);
  const [confirmation, setConfirmation] = useState(null);

  // Async results are only applied if they are still for the visible account
  const visibleAccountRef = useRef(null);
  const loadSequenceRef = useRef(0);
  const timeRangeRef = useRef(timeRange);

  const notify = useCallback((message, type = 'success') => {
    toastCounter += 1;
    const id = toastCounter;
    // A repeated message replaces the earlier copy instead of stacking up
    setToasts((prev) => [...prev.filter((toast) => toast.message !== message), { id, message, type }]);
  }, []);

  const dismissToast = useCallback((id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const clearVisibleData = () => {
    setAllInvoices([]);
    setDetailedLineItems([]);
    setProcessedData(null);
    setError('');
    setWarning('');
    setStatusMessage('');
  };

  // Put an account's data on screen and remember it for quick switching
  const showAccountData = (accountId, invoices, lineItems) => {
    const filteredLineItems = filterLineItemsByTimeRange(lineItems, timeRangeRef.current);
    setAllInvoices(invoices);
    setDetailedLineItems(lineItems);
    setProcessedData(processCSVDataForVisualizations(filteredLineItems));
    setAccountsData((prevData) => ({
      ...prevData,
      [accountId]: { invoices, detailedLineItems: lineItems }
    }));
  };

  // Fetch data from the DigitalOcean API
  const fetchDataFromAPI = async (account, { isBackgroundRefresh = false, announce = false } = {}) => {
    const accountId = account.name;
    const sequence = loadSequenceRef.current;
    const isVisible = () => visibleAccountRef.current === accountId;
    const isLatest = () => isVisible() && loadSequenceRef.current === sequence;

    if (!isBackgroundRefresh) {
      setIsLoading(true);
      setLoadProgress({ done: 0, total: 0 });
      setError('');
    }

    try {
      const { invoices, lineItems, failedInvoices = [] } = await fetchAllInvoiceData(account.token, {
        onProgress: (done, total) => {
          if (!isBackgroundRefresh && isLatest()) setLoadProgress({ done, total });
        }
      });

      // Only complete data is cached, so a partial load is never mistaken for the full picture
      if (invoices.length > 0 && failedInvoices.length === 0) {
        saveData(accountId, 'invoices', invoices);
        saveData(accountId, 'csvLineItems', lineItems);
      }

      // The user switched accounts while this was loading
      if (!isVisible()) return;

      if (invoices.length === 0) {
        setAllInvoices([]);
        setDetailedLineItems([]);
        setProcessedData(null);
        setStatusMessage('');
        setCacheStatus({ isCached: false });
        return;
      }

      showAccountData(accountId, invoices, lineItems);
      setCacheStatus(getCacheStatus(accountId));
      setError('');
      setWarning(
        failedInvoices.length > 0
          ? `${failedInvoices.length} of ${invoices.length} invoices could not be loaded, so the totals below are incomplete. Use Refresh Data to try again.`
          : ''
      );
      setStatusMessage(isBackgroundRefresh ? `Data refreshed at ${new Date().toLocaleString()}` : '');
      if (announce && failedInvoices.length === 0) notify(`Billing data for ${accountId} is up to date.`);
    } catch (apiError) {
      console.error('API Error:', apiError);
      if (!isVisible()) return;

      if (isBackgroundRefresh) {
        // Don't disrupt the user; the cached data stays on screen
        setStatusMessage('Background refresh failed. Showing the most recent cached data.');
        return;
      }

      const message = describeDigitalOceanError(apiError);
      const cachedLineItems = loadData(accountId, 'csvLineItems');
      const cachedInvoices = loadData(accountId, 'invoices');

      if (cachedLineItems?.data && cachedInvoices?.data) {
        const status = describeCacheEntry(cachedLineItems);
        showAccountData(accountId, cachedInvoices.data, cachedLineItems.data);
        setCacheStatus(status);
        setError(`${message} Showing cached data from ${status.formattedDate}.`);
        setWarning(
          status.isReduced
            ? `Only the ${status.itemCount.toLocaleString()} most recent line items fit in this browser's cache, so totals for longer time ranges are incomplete until the data can be reloaded.`
            : ''
        );
      } else {
        setError(message);
      }
    } finally {
      if (!isBackgroundRefresh && isLatest()) setIsLoading(false);
    }
  };

  // Load an account's data, from the browser cache when it is fresh
  const loadAccountData = async (account, { forceRefresh = false, announce = false } = {}) => {
    const accountId = account.name;
    visibleAccountRef.current = accountId;
    loadSequenceRef.current += 1;
    setError('');
    setWarning('');
    setStatusMessage('');

    if (!forceRefresh) {
      const cachedLineItems = loadData(accountId, 'csvLineItems');
      const status = describeCacheEntry(cachedLineItems);
      setCacheStatus(status);

      // A reduced cache is incomplete, so it is only used as an offline fallback
      if (status.isCached && !status.isStale && !status.isReduced) {
        const cachedInvoices = loadData(accountId, 'invoices');
        if (Array.isArray(cachedLineItems.data) && cachedInvoices?.data) {
          showAccountData(accountId, cachedInvoices.data, cachedLineItems.data);
          setIsLoading(false);

          // If it's getting stale, refresh in the background
          if (status.hoursSinceUpdate > BACKGROUND_REFRESH_AFTER_HOURS) {
            setStatusMessage(`Using cached data from ${status.formattedDate}. Refreshing in background...`);
            fetchDataFromAPI(account, { isBackgroundRefresh: true });
          } else {
            setStatusMessage(`Using cached data from ${status.formattedDate}`);
          }
          return;
        }
      }
    }

    await fetchDataFromAPI(account, { announce });
  };

  const checkSession = useCallback(async () => {
    setAuthState('checking');
    try {
      const session = await apiRequest('/api/session');
      if (session && session.authenticated) {
        setUser(session.user);
        setAuthState('signedIn');
      } else {
        setUser(null);
        setAuthState('signedOut');
      }
    } catch (err) {
      setAuthMessage(err.message);
      setAuthState('unreachable');
    }
  }, []);

  useEffect(() => {
    removeLegacyAuthStorage();
    checkSession();
  }, [checkSession]);

  // After sign-in: collect accounts and load the first one
  useEffect(() => {
    if (authState !== 'signedIn') return undefined;
    let cancelled = false;

    (async () => {
      let managedAccounts = [];
      try {
        const response = await apiRequest('/api/accounts');
        managedAccounts = (response && response.accounts) || [];
      } catch (err) {
        if (cancelled) return;
        if (err.status === 401) {
          setAuthMessage('Your session has expired. Please sign in again.');
          setUser(null);
          setAuthState('signedOut');
          return;
        }
        notify(`Accounts configured on the server could not be loaded. ${err.message}`, 'error');
      }
      if (cancelled) return;

      const merged = mergeAccounts(
        [...managedAccounts, ...(parseEnvironmentAccounts() || [])],
        readStoredAccounts()
      );
      persistAccounts(merged);
      setAccounts(merged);
      setCurrentAccountIndex(0);
      setAccountsLoaded(true);

      if (merged.length > 0) {
        loadAccountData(merged[0]);
      }
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authState]);

  const handleAuthSuccess = (signedInUser) => {
    setAuthMessage('');
    setUser(signedInUser || null);
    setAuthState('signedIn');
  };

  // Handle adding a new account
  const handleAddAccount = (name, token) => {
    if (accounts.some((account) => account.name.toLowerCase() === name.toLowerCase())) {
      notify(`An account named "${name}" already exists. Choose a different name.`, 'error');
      return;
    }

    const newAccount = { name, token };
    const updatedAccounts = [...accounts, newAccount];
    setAccounts(updatedAccounts);
    persistAccounts(updatedAccounts);

    // Show the account that was just added
    setCurrentAccountIndex(updatedAccounts.length - 1);
    clearVisibleData();
    loadAccountData(newAccount);
    notify(`Account "${name}" added.`);
  };

  // Handle switching between accounts
  const handleAccountSwitch = (index) => {
    if (index < 0 || index >= accounts.length || index === currentAccountIndex) return;

    setCurrentAccountIndex(index);
    const account = accounts[index];
    const accountId = account.name;
    const inMemory = accountsData[accountId];

    if (!inMemory) {
      clearVisibleData();
      loadAccountData(account);
      return;
    }

    // Use the data already loaded in this session
    visibleAccountRef.current = accountId;
    loadSequenceRef.current += 1;
    setIsLoading(false);
    setError('');
    setWarning('');
    showAccountData(accountId, inMemory.invoices, inMemory.detailedLineItems);

    const status = getCacheStatus(accountId);
    setCacheStatus(status);
    if (status.isCached && status.isStale) {
      setStatusMessage(`Using cached data from ${status.formattedDate}. Refreshing in background...`);
      fetchDataFromAPI(account, { isBackgroundRefresh: true });
    } else {
      setStatusMessage(status.isCached ? `Using cached data from ${status.formattedDate}` : '');
    }
  };

  const removeAccount = (index) => {
    const removedAccount = accounts[index];
    if (!removedAccount) return;

    const updatedAccounts = accounts.filter((_, i) => i !== index);
    setAccounts(updatedAccounts);
    persistAccounts(updatedAccounts);

    // Also remove this account's data from memory and browser storage
    setAccountsData((prevData) => {
      const newData = { ...prevData };
      delete newData[removedAccount.name];
      return newData;
    });
    clearAccountData(removedAccount.name);
    notify(`Account "${removedAccount.name}" removed.`);

    if (updatedAccounts.length === 0) {
      // No accounts left, go back to the connect form
      visibleAccountRef.current = null;
      setCurrentAccountIndex(0);
      clearVisibleData();
    } else if (index === currentAccountIndex) {
      // Current account was removed, switch to first account
      setCurrentAccountIndex(0);
      const firstAccount = updatedAccounts[0];
      const inMemory = accountsData[firstAccount.name];
      if (inMemory) {
        visibleAccountRef.current = firstAccount.name;
        loadSequenceRef.current += 1;
        setIsLoading(false);
        setError('');
        setWarning('');
        showAccountData(firstAccount.name, inMemory.invoices, inMemory.detailedLineItems);
        const status = getCacheStatus(firstAccount.name);
        setCacheStatus(status);
        setStatusMessage(status.isCached ? `Using cached data from ${status.formattedDate}` : '');
      } else {
        clearVisibleData();
        loadAccountData(firstAccount);
      }
    } else if (index < currentAccountIndex) {
      // Account before current was removed, adjust index
      setCurrentAccountIndex(currentAccountIndex - 1);
    }
  };

  // Removing an account deletes its token from this browser, so confirm first
  const handleRemoveAccount = (index) => {
    const account = accounts[index];
    if (!account || account.managed) return;
    setConfirmation({
      title: 'Remove account?',
      message: `"${account.name}" and its API token will be removed from this browser, along with its cached billing data. You can add the account again later with its token.`,
      confirmLabel: 'Remove account',
      tone: 'danger',
      onConfirm: () => removeAccount(index)
    });
  };

  const performLogout = async () => {
    try {
      await apiRequest('/api/logout', { method: 'POST' });
    } catch (err) {
      notify(`Could not sign out: ${err.message}`, 'error');
      return;
    }

    // Forget tokens and billing data kept in this browser
    accounts.forEach((account) => clearAccountData(account.name));
    try { localStorage.removeItem(ACCOUNTS_KEY); } catch (e) {}
    visibleAccountRef.current = null;
    loadSequenceRef.current += 1;
    setAccounts([]);
    setAccountsLoaded(false);
    setCurrentAccountIndex(0);
    setAccountsData({});
    setIsLoading(false);
    setCacheStatus({ isCached: false });
    clearVisibleData();
    setToasts([]);
    setUser(null);
    setAuthMessage('You have been signed out.');
    setAuthState('signedOut');
  };

  // Handle logout (all accounts)
  const handleLogout = () => {
    const ownAccounts = accounts.filter((account) => !account.managed);
    if (ownAccounts.length === 0) {
      performLogout();
      return;
    }
    setConfirmation({
      title: 'Sign out?',
      message: `Signing out removes the ${ownAccounts.length === 1 ? 'API token' : `${ownAccounts.length} API tokens`} you added in this browser (${ownAccounts.map((a) => a.name).join(', ')}). You will need to add ${ownAccounts.length === 1 ? 'it' : 'them'} again next time.`,
      confirmLabel: 'Sign out',
      tone: 'danger',
      onConfirm: performLogout
    });
  };

  // Handle refresh (current account)
  const handleRefresh = () => {
    const account = accounts[currentAccountIndex];
    if (account) loadAccountData(account, { forceRefresh: true, announce: true });
  };

  // Handle clearing cache (current account)
  const handleClearCache = () => {
    const account = accounts[currentAccountIndex];
    if (!account) return;
    setConfirmation({
      title: 'Clear cached data?',
      message: `Cached billing data for "${account.name}" will be deleted from this browser and downloaded again from DigitalOcean.`,
      confirmLabel: 'Clear cache',
      onConfirm: () => {
        clearAccountData(account.name);
        setAccountsData((prevData) => {
          const newData = { ...prevData };
          delete newData[account.name];
          return newData;
        });
        setCacheStatus({ isCached: false });
        loadAccountData(account, { forceRefresh: true, announce: true });
      }
    });
  };

  // Handle time range change
  const handleTimeRangeChange = (newRange) => {
    setTimeRange(newRange);
    timeRangeRef.current = newRange;

    // Recalculate processed data for the new time range if we have line items
    if (detailedLineItems.length > 0) {
      const filteredLineItems = filterLineItemsByTimeRange(detailedLineItems, newRange) || [];
      setProcessedData(processCSVDataForVisualizations(filteredLineItems));
    }
  };

  const closeConfirmation = () => setConfirmation(null);
  const currentAccount = accounts[currentAccountIndex];

  const showsDashboard = authState === 'signedIn' && accountsLoaded && accounts.length > 0;

  let content;
  if (authState === 'checking' || (authState === 'signedIn' && !accountsLoaded)) {
    content = (
      <div className="form-container app-status" role="status" aria-live="polite">
        <div className="loading-spinner" aria-hidden="true"></div>
        {authState === 'checking' ? 'Checking your session...' : 'Loading your accounts...'}
      </div>
    );
  } else if (authState === 'unreachable') {
    content = (
      <div className="form-container" role="alert">
        <h2 className="form-title">Can't reach the dashboard</h2>
        <p className="intro-text">{authMessage}</p>
        <button type="button" style={{ width: '100%' }} onClick={checkSession}>Try again</button>
      </div>
    );
  } else if (authState === 'signedOut') {
    content = <UserLogin onAuthSuccess={handleAuthSuccess} notice={authMessage} />;
  } else if (accounts.length === 0) {
    content = (
      <LoginForm
        onAddAccount={handleAddAccount}
        onSignOut={handleLogout}
        userEmail={user && user.email}
      />
    );
  } else {
    content = (
      <Dashboard
        accountName={currentAccount?.name || 'Unknown Account'}
        allInvoices={allInvoices}
        detailedLineItems={detailedLineItems}
        processedData={processedData}
        isLoading={isLoading}
        loadProgress={loadProgress}
        error={error}
        warning={warning}
        statusMessage={statusMessage}
        cacheStatus={cacheStatus}
        apiToken={currentAccount?.token}
        timeRange={timeRange}
        userEmail={user && user.email}
        headerActions={
          <AccountSelector
            accounts={accounts}
            currentIndex={currentAccountIndex}
            onSwitchAccount={handleAccountSwitch}
            onRemoveAccount={handleRemoveAccount}
            onAddAccount={handleAddAccount}
          />
        }
        onLogout={handleLogout}
        onRefresh={handleRefresh}
        onClearCache={handleClearCache}
        onTimeRangeChange={handleTimeRangeChange}
        onDismissError={() => setError('')}
      />
    );
  }

  return (
    <div className="App">
      {/* The dashboard has the theme switch in its header; other screens get it in the corner */}
      {!showsDashboard && <ThemeToggle className="theme-toggle-floating" />}
      <ErrorBoundary>{content}</ErrorBoundary>

      <ConfirmDialog
        isOpen={Boolean(confirmation)}
        title={confirmation ? confirmation.title : ''}
        message={confirmation ? confirmation.message : ''}
        confirmLabel={confirmation ? confirmation.confirmLabel : undefined}
        tone={confirmation ? confirmation.tone : undefined}
        onCancel={closeConfirmation}
        onConfirm={() => {
          const action = confirmation && confirmation.onConfirm;
          closeConfirmation();
          if (action) action();
        }}
      />

      <ToastContainer toasts={toasts} onDismiss={dismissToast} />
    </div>
  );
}

export default App;
